import React, { useCallback, useEffect, useState } from "react";
import {
    Avatar,
    Box,
    Button,
    CircularProgress,
    Typography,
} from "@mui/material";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { format, formatDistanceToNow } from "date-fns";
import { Link } from "react-router-dom";
import { publicApi } from "../../api/http";
import { authAxios, authRequestHeaders, useAuth } from "../../contexts/AuthContext";
import { userDisplayName, userInitial } from "../../utils/userDisplayName";
import "../../pages/Player/Player.css";
import "./ReviewDiscussion.css";

function parseApiUtcDate(dateStr) {
    if (!dateStr) return null;
    const trimmed = dateStr.trim();
    if (/Z$|[+-]\d{2}:\d{2}$|[+-]\d{4}$/.test(trimmed)) {
        const parsed = new Date(trimmed);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    const iso = trimmed.includes("T") ? trimmed : trimmed.replace(" ", "T");
    const parsed = new Date(`${iso}Z`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatCommentDate(dateStr) {
    const date = parseApiUtcDate(dateStr);
    if (!date) return dateStr || "";

    const now = Date.now();
    const diffMs = now - date.getTime();
    if (diffMs < 0) {
        return "just now";
    }

    const daysAgo = diffMs / (1000 * 60 * 60 * 24);
    if (daysAgo < 7) {
        return formatDistanceToNow(date, { addSuffix: true });
    }
    return format(date, "MMM d, yyyy");
}

/**
 * @param {object} props
 * @param {"media"|"independent"} props.reviewType
 * @param {string} props.reviewId
 * @param {string} [props.title] - section heading override
 */
function ReviewDiscussionSection({ reviewType, reviewId, title = "Discussion" }) {
    const { user, loading: authLoading, ensureAccessToken } = useAuth();
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState("");
    const [newBody, setNewBody] = useState("");
    const [posting, setPosting] = useState(false);
    const [postError, setPostError] = useState("");
    const [actionId, setActionId] = useState(null);

    const basePath = `/review-discussion/${reviewType}/${reviewId}`;

    const loadComments = useCallback(async () => {
        if (!reviewId || !reviewType) return;
        setLoading(true);
        setLoadError("");
        try {
            const token = await ensureAccessToken();
            const res = token
                ? await authAxios.get(basePath, { headers: authRequestHeaders(token) })
                : await publicApi.get(basePath);
            setComments(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.warn("[review-discussion] load failed", err.response?.status, err.response?.data);
            setComments([]);
            setLoadError("Could not load discussion. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [reviewId, reviewType, basePath, ensureAccessToken]);

    useEffect(() => {
        if (!reviewId || !reviewType) return;
        loadComments();
    }, [reviewId, reviewType, loadComments, authLoading]);

    const handlePost = async (e) => {
        e.preventDefault();
        const body = newBody.trim();
        if (!body || posting || !user) return;

        setPosting(true);
        setPostError("");
        try {
            const token = await ensureAccessToken();
            if (!token) {
                setPostError("Sign in to post a comment.");
                return;
            }
            const res = await authAxios.post(
                basePath,
                { body },
                { headers: authRequestHeaders(token) },
            );
            setComments((prev) => [res.data, ...prev]);
            setNewBody("");
        } catch (err) {
            const detail = err.response?.data?.detail;
            setPostError(
                typeof detail === "string" ? detail : "Failed to post comment. Please try again.",
            );
        } finally {
            setPosting(false);
        }
    };

    const toggleLike = async (comment) => {
        if (!user || actionId) return;
        setActionId(comment.id);
        try {
            const token = await ensureAccessToken();
            if (!token) return;

            const headers = authRequestHeaders(token);
            const res = comment.liked_by_me
                ? await authAxios.delete(`/review-discussion/${comment.id}/like`, { headers })
                : await authAxios.post(`/review-discussion/${comment.id}/like`, null, { headers });

            const likeCount = res.data?.like_count ?? comment.like_count;
            const likedByMe = comment.liked_by_me
                ? !res.data?.unliked
                : res.data?.liked === true;

            setComments((prev) =>
                prev.map((c) =>
                    c.id === comment.id
                        ? { ...c, like_count: likeCount, liked_by_me: likedByMe }
                        : c,
                ),
            );
        } catch (err) {
            console.warn("[review-discussion] like failed", err.response?.status, err.response?.data);
        } finally {
            setActionId(null);
        }
    };

    const handleDelete = async (commentId) => {
        if (!user || actionId) return;
        setActionId(commentId);
        try {
            const token = await ensureAccessToken();
            if (!token) return;
            await authAxios.delete(`/review-discussion/${commentId}`, {
                headers: authRequestHeaders(token),
            });
            setComments((prev) => prev.filter((c) => c.id !== commentId));
        } catch (err) {
            console.warn("[review-discussion] delete failed", err.response?.status, err.response?.data);
        } finally {
            setActionId(null);
        }
    };

    if (!reviewId) {
        return null;
    }

    return (
        <Box className="review-discussion-section">
            <Box className="discussion-compose review-discussion-compose">
                <Box className="discussion-section-header">
                    <RateReviewOutlinedIcon className="discussion-section-icon" aria-hidden />
                    <Box>
                        <Typography component="h2" className="discussion-section-title">
                            {title}
                            {comments.length > 0 && (
                                <span className="review-discussion-count"> [{comments.length}]</span>
                            )}
                        </Typography>
                        <Typography className="discussion-section-subtitle" variant="body2">
                            Share your take on this ranking with the community.
                        </Typography>
                    </Box>
                </Box>

                {user ? (
                    <Box component="form" onSubmit={handlePost} className="discussion-compose-card">
                        <Avatar
                            src={user.picture_url || undefined}
                            alt={userDisplayName(user)}
                            className="discussion-compose-avatar"
                        >
                            {userInitial(user)}
                        </Avatar>
                        <Box className="discussion-compose-fields">
                            <textarea
                                className="discussion-textarea"
                                rows={2}
                                placeholder="What do you think of this ranking?"
                                value={newBody}
                                onChange={(e) => setNewBody(e.target.value)}
                                disabled={posting}
                                maxLength={5000}
                            />
                            <Box className="discussion-compose-footer">
                                <Typography className="discussion-char-count" variant="caption">
                                    {newBody.length}/5000
                                </Typography>
                                <Button
                                    type="submit"
                                    size="small"
                                    className="discussion-post-btn"
                                    disabled={posting || !newBody.trim()}
                                    disableElevation
                                    endIcon={
                                        posting ? (
                                            <CircularProgress size={14} color="inherit" />
                                        ) : (
                                            <SendRoundedIcon sx={{ fontSize: "0.9rem" }} />
                                        )
                                    }
                                >
                                    {posting ? "Posting…" : "Post comment"}
                                </Button>
                            </Box>
                            {postError && (
                                <Typography className="discussion-error" variant="body2">
                                    {postError}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                ) : (
                    <Box className="discussion-sign-in-card">
                        <ChatBubbleOutlineRoundedIcon className="discussion-sign-in-icon" aria-hidden />
                        <Typography className="discussion-sign-in-text" variant="body2">
                            <Link to="/login" className="discussion-sign-in-link">Sign in</Link>
                            {" "}to join the conversation.
                        </Typography>
                    </Box>
                )}
            </Box>

            <Box className="discussion-list-scroll review-discussion-list">
                {loading && (
                    <Box className="discussion-loading">
                        <CircularProgress size={28} sx={{ color: "var(--brand-strong)" }} />
                        <Typography variant="body2">Loading discussion…</Typography>
                    </Box>
                )}

                {!loading && loadError && (
                    <Box className="discussion-empty-state discussion-empty-state--error">
                        <Typography variant="body1">{loadError}</Typography>
                        <Button className="discussion-retry-btn" onClick={loadComments} disableElevation>
                            Try again
                        </Button>
                    </Box>
                )}

                {!loading && !loadError && comments.length === 0 && (
                    <Box className="discussion-empty-state">
                        <ChatBubbleOutlineRoundedIcon className="discussion-empty-icon" aria-hidden />
                        <Typography className="discussion-empty-title" variant="subtitle1">
                            No comments yet
                        </Typography>
                        <Typography className="discussion-empty-caption" variant="body2">
                            {user
                                ? "Be the first to share your thoughts on this ranking."
                                : "Sign in and be the first to start the thread."}
                        </Typography>
                    </Box>
                )}

                {!loading && !loadError && comments.length > 0 && (
                    <Box className="discussion-comments-list">
                        {comments.map((comment) => {
                            const author = comment.author;
                            const isOwner = user && author?.id === user.id;
                            const isBusy = actionId === comment.id;
                            const likeCount = comment.like_count || 0;

                            return (
                                <article
                                    key={comment.id}
                                    className={`discussion-comment-card${isOwner ? " discussion-comment-card--own" : ""}`}
                                >
                                    <Avatar
                                        src={author?.picture_url || undefined}
                                        alt={userDisplayName(author)}
                                        className="discussion-comment-avatar"
                                    >
                                        {userInitial(author)}
                                    </Avatar>
                                    <Box className="discussion-comment-content">
                                        <Box className="discussion-comment-header">
                                            <Box className="discussion-comment-meta">
                                                <Link
                                                    to={author?.id ? `/profile/${author.id}` : "#"}
                                                    className="discussion-author-name"
                                                >
                                                    {userDisplayName(author)}
                                                </Link>
                                                {isOwner && (
                                                    <span className="discussion-you-badge">You</span>
                                                )}
                                                <Typography
                                                    component="span"
                                                    className="discussion-comment-date"
                                                >
                                                    {formatCommentDate(comment.created_at)}
                                                </Typography>
                                            </Box>
                                            {isOwner && (
                                                <button
                                                    type="button"
                                                    className="discussion-delete-btn"
                                                    onClick={() => handleDelete(comment.id)}
                                                    disabled={isBusy}
                                                    aria-label="Delete comment"
                                                >
                                                    {isBusy ? (
                                                        <CircularProgress size={12} />
                                                    ) : (
                                                        <DeleteOutlineRoundedIcon sx={{ fontSize: "0.95rem" }} />
                                                    )}
                                                </button>
                                            )}
                                        </Box>
                                        <Typography className="discussion-comment-text" component="p">
                                            {comment.body}
                                        </Typography>
                                        <Box className="discussion-comment-actions">
                                            <button
                                                type="button"
                                                className={`discussion-like-pill${comment.liked_by_me ? " discussion-like-pill--active" : ""}`}
                                                onClick={() => toggleLike(comment)}
                                                disabled={!user || isBusy}
                                                aria-label={comment.liked_by_me ? "Unlike comment" : "Like comment"}
                                            >
                                                {isBusy ? (
                                                    <CircularProgress size={12} />
                                                ) : comment.liked_by_me ? (
                                                    <ThumbUpIcon fontSize="inherit" />
                                                ) : (
                                                    <ThumbUpAltOutlinedIcon fontSize="inherit" />
                                                )}
                                                <span>{likeCount}</span>
                                            </button>
                                        </Box>
                                    </Box>
                                </article>
                            );
                        })}
                    </Box>
                )}
            </Box>
        </Box>
    );
}

export default ReviewDiscussionSection;
